import React from "react";
import Card from "../ui/Card";

type DriverPickupsWeekProps = {
  weekOffset: number;
};

const weekGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  overflow: "hidden",
  marginTop: "16px",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const subtitleStyle: React.CSSProperties = {
  marginTop: "4px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const dayStyle = (isToday: boolean): React.CSSProperties => ({
  position: "relative",
  minHeight: "170px",
  padding: "16px 12px",
  borderRight: "1px solid var(--border)",
  background: isToday ? "var(--primary-soft)" : "var(--surface-alt)",
  boxShadow: isToday ? "inset 0 0 0 1px var(--primary)" : "none",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
});

const todayBadgeStyle: React.CSSProperties = {
  position: "absolute",
  top: "-1px",
  right: "10px",
  padding: "4px 9px",
  borderRadius: "0 0 9px 9px",
  background: "var(--primary)",
  color: "#151515",
  fontSize: "10px",
  fontWeight: 800,
};

const dayNameStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 800,
  color: "var(--text-primary)",
  textTransform: "uppercase",
};

const dateStyle: React.CSSProperties = {
  marginTop: "3px",
  fontSize: "12px",
  color: "var(--text-secondary)",
};

const statusCircleStyle: React.CSSProperties = {
  width: "44px",
  height: "44px",
  marginTop: "18px",
  borderRadius: "50%",
  border: "1px dashed var(--text-secondary)",
  color: "var(--text-secondary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  fontWeight: 700,
};

const statusTextStyle: React.CSSProperties = {
  marginTop: "14px",
  padding: "5px 9px",
  borderRadius: "8px",
  background: "var(--surface)",
  color: "var(--text-secondary)",
  fontSize: "11px",
  fontWeight: 700,
};

const legendStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "14px",
  color: "var(--text-secondary)",
  fontSize: "12px",
};

const legendDotStyle: React.CSSProperties = {
  width: "10px",
  height: "10px",
  borderRadius: "50%",
  border: "1px dashed var(--text-secondary)",
};

const dateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getWeek = (weekOffset: number): Date[] => {
  const today = new Date();
  const mondayOffset = today.getDay() === 0 ? -6 : 1 - today.getDay();

  const monday = new Date(today);
  monday.setHours(12, 0, 0, 0);
  monday.setDate(today.getDate() + mondayOffset + weekOffset * 7);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return date;
  });
};

const DriverPickupsWeek = ({
  weekOffset,
}: DriverPickupsWeekProps) => {
  const todayISO = dateToISO(new Date());
  const weekDays = getWeek(weekOffset);

  return (
    <Card>
      <div style={headerStyle}>
        <div>
          <div style={titleStyle}>Seguimiento de 7 días</div>
          <div style={subtitleStyle}>
            Presentismo y colectas pendientes de validación
          </div>
        </div>
      </div>

      <div style={weekGridStyle}>
        {weekDays.map((date) => {
          const iso = dateToISO(date);
          const isToday = iso === todayISO;

          const dayName = new Intl.DateTimeFormat("es-AR", {
            weekday: "short",
          })
            .format(date)
            .replace(".", "");

          const formattedDate = new Intl.DateTimeFormat("es-AR", {
            day: "2-digit",
            month: "2-digit",
          }).format(date);

          return (
            <div key={iso} style={dayStyle(isToday)}>
              {isToday ? <span style={todayBadgeStyle}>Hoy</span> : null}

              <div style={dayNameStyle}>{dayName}</div>
              <div style={dateStyle}>{formattedDate}</div>

              <div style={statusCircleStyle}>—</div>
              <div style={statusTextStyle}>Sin verificar</div>
            </div>
          );
        })}
      </div>

      <div style={legendStyle}>
        <span style={legendDotStyle} />
        Sin información recibida del control de presentismo
      </div>
    </Card>
  );
};

export default DriverPickupsWeek;